import { useNavigate } from 'react-router-dom';
import { useQuery } from '@apollo/client';

import { ViewerDocument } from '@sandtech-hub-monorepo/ehub-client';

import { PageHero } from '../../components/PageHero';
import { AppRoutesPaths } from '../../routes/types';
import welcomeHero from '../../assets/gsa/welcome-hero.webp';
import pathGsa from '../../assets/gsa/path-gsa.webp';
import pathDegree from '../../assets/gsa/path-degree.webp';
import { useLearnerPath } from '../../data/useLearnerPath';
import { useSubscriptionState } from '../../data/useSubscriptionState';
import { SubscriptionBanner } from '../GsaDashboard/components/SubscriptionBanner';
import { ProgramPathCard } from './components/ProgramPathCard';

export const ProgramPathChooser = () => {
  const navigate = useNavigate();
  const { data } = useQuery(ViewerDocument, { fetchPolicy: 'cache-first' });
  const firstName = data?.viewer?.firstName;

  /*
   * A GSA learner sees the undergraduate card but cannot open it. It stays on
   * the page because it is the only place the degree path is described, and
   * the guard would turn them away from /undergraduate anyway — better a
   * button that plainly does not work than one that bounces you back.
   *
   * Degree learners never reach this page: `/` renders their own home.
   */
  const path = useLearnerPath();
  const degreeOpen = path !== 'gsa';

  /*
   * Paying and enrolling are independent, so a learner can hold a live
   * subscription and still be standing here with no program. Without this the
   * page says nothing about it and the only confirmation they have paid is the
   * Settings tab.
   *
   * `none` is the one state left out. It is an upsell, and this page exists to
   * pick a path, not to sell one before the learner has chosen — every other
   * state is telling them something about a subscription they already have.
   */
  const { state, daysRemaining, loading: subscriptionLoading } =
    useSubscriptionState();

  return (
    <div className="bg-background">
      <PageHero
        backgroundImage={welcomeHero}
        showDecorations
      >
        <h1 className="text-4xl md:text-5xl font-semibold">
          Welcome{firstName ? `, ${firstName}` : ''}.
        </h1>
        <p className="mt-4 text-xl md:text-2xl">
          What will you make possible?
        </p>
      </PageHero>

      <div className="mx-auto w-full max-w-screen-2xl px-4 sm:px-8 md:px-12 py-12 md:py-16">
        {state !== 'none' && (
          <div className="mb-10">
            <SubscriptionBanner
              state={state}
              daysRemaining={daysRemaining}
              programId={undefined}
              loading={subscriptionLoading}
            />
          </div>
        )}

        <h2 className="text-center text-3xl md:text-5xl font-semibold text-heading">
          Here is a clear look at both paths to help you decide.
        </h2>

        <div className="mt-10 flex flex-col items-stretch justify-center gap-7 lg:flex-row">
          <ProgramPathCard
            image={pathGsa}
            badge="Available"
            title="Global Skills Academy"
            subtitle="Practical, career-ready skills that put you ahead, in AI and beyond."
            bullets={[
              'Self-paced, most learners finish in 3–6 months',
              "Start free, upgrade when you're ready",
              'Finish with a credential employers recognize',
              'Credits count toward a future FIT degree',
            ]}
            ctaLabel="Get Started"
            ctaVariant="default"
            onExplore={() => navigate(AppRoutesPaths.Programs)}
          />
          <ProgramPathCard
            image={pathDegree}
            badge="Coming Soon"
            title="Undergraduate Programs"
            subtitle="A full degree, built around your life."
            bullets={[
              'BS in Software Engineering or BS in Data Science',
              'Fully accredited, online-first, 3 years to complete',
              'Build your own business idea in the Venture Lab from day one',
              'From $200/month',
            ]}
            ctaLabel="Join the Waitlist"
            ctaVariant="outline"
            disabled={!degreeOpen}
            onExplore={() => navigate(AppRoutesPaths.Undergraduate)}
          />
        </div>
      </div>
    </div>
  );
};
